import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { XIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CustomerAddressDTO } from '../../../server/dto/customer-address-dto.ts';
import { api } from '../../../utils/api.ts';
import { SearchCombobox } from '../../shared/components/search-combobox/search-combobox.tsx';

type EventAddressSelectProps = {
    customerId?: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
};

function getAddressTypeLabel(type: CustomerAddressDTO['type'], t: (key: string) => string): string {
    if (type === 'BILLING') {
        return t('events__form_address_type_bill_to');
    }
    if (type === 'SHIPPING') {
        return t('events__form_address_type_ship_to');
    }
    return t('events__form_address_type_primary');
}

export function EventAddressSelect({ customerId, value, onChange, disabled }: EventAddressSelectProps) {
    const { t } = useLocale('events');

    const { data: customerAddresses, isFetching } = api.platoUser.getManyCustomerAddress.get.useQuery(
        {
            customerId: customerId ?? '',
            types: [SrCustomerAddressType.BILLING, SrCustomerAddressType.SHIPPING],
        },
        { enabled: !!customerId },
    );

    const groupedItems = useMemo(() => {
        if (!customerAddresses) {
            return [];
        }

        return [
            {
                id: 'addresses',
                items: customerAddresses,
            },
        ];
    }, [customerAddresses]);

    const selectedAddressId =
        customerAddresses?.find((a) => getFullAddressString(a) === value)?.addressId ?? '';

    return (
        <SearchCombobox<CustomerAddressDTO>
            className="w-full"
            variant="inline"
            isLoading={isFetching}
            groupedItems={groupedItems}
            notFoundPlaceholder=""
            inputPlaceholder={t('events__form_location_placeholder')}
            keySelector={(item) => item.addressId}
            valueSelector={(item) => item.addressId}
            keywordsSelector={(item) => [getFullAddressString(item), getAddressTypeLabel(item.type, t)]}
            filter={(_value, search, keywords) => {
                const text = keywords?.join(' ') ?? '';
                if (text.toLowerCase().includes(search.toLowerCase())) {
                    return 1;
                }
                return 0;
            }}
            renderItem={(item) => (
                <div className="flex w-full items-center justify-between gap-2">
                    <Text className="truncate">{getFullAddressString(item)}</Text>
                    <Text color="neutral-500" className="shrink-0 text-xs">
                        {getAddressTypeLabel(item.type, t)}
                    </Text>
                </div>
            )}
            renderSelectedItem={(item) => <Text className="truncate">{getFullAddressString(item)}</Text>}
            onValueChange={(_id, item) => {
                onChange(item ? getFullAddressString(item) : '');
            }}
            selectedValue={selectedAddressId}
            disabled={disabled}
            enableCreation
            createLabel={(input) => (
                <Text>{t('events__form_location_use_custom_address', { address: input })}</Text>
            )}
            onCreate={(input) => {
                onChange(input);
            }}
        >
            <SearchCombobox.TriggerButton
                variant="inline"
                disabled={disabled}
                isLoading={isFetching}
                className="h-8"
                aria-label={t('events__form_location_placeholder')}
            >
                {value ? (
                    <div className="flex w-full items-center gap-1">
                        <Text className="truncate flex-1">{value}</Text>
                        {!disabled && (
                            <Button
                                variant="ghost"
                                icon={XIcon}
                                className="ml-auto shrink-0 p-0 h-6 w-6 min-w-6 opacity-50 hover:opacity-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange('');
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <Text color="neutral-600">{t('events__form_location_placeholder')}</Text>
                )}
            </SearchCombobox.TriggerButton>
        </SearchCombobox>
    );
}
